// src/pages/student/ProgressCard.js
import React, { useEffect, useMemo, useState,useCallback  } from "react";
import {
  Card, Row, Col, Badge, Button, ProgressBar,
  Toast, ToastContainer, Alert, Table, ListGroup, Form
} from "react-bootstrap";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import {
  collection, collectionGroup, doc, getDoc, getDocs,
  onSnapshot, query, where, limit
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../services/firebase";

/* ---------------- utils ---------------- */
dayjs.extend(relativeTime);
dayjs.extend(duration);

const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const safeTxt = (v) => (v == null || typeof v === "object" ? "" : String(v));
const pct = (n, d) => {
  const N = Number(n || 0);
  const D = Number(d || 0);
  if (!D) return 0;
  const p = Math.round((100 * N) / D);
  return isFinite(p) ? Math.max(0, Math.min(100, p)) : 0;
};
const uniqPushIntoMap = (store, docs) => {
  docs.forEach((d) =>
    store.set(d.id, d.data ? { id: d.id, ...d.data() } : d)
  );
  return Array.from(store.values());
};

// color by percent helper
const variantByPct = (v) => (v >= 75 ? "success" : v >= 50 ? "warning" : "danger");
// normalize the run mode ("live" | "mock")
const runModeOf = (src) =>
  String(
    src?.examMode ??
    src?.mode ??
    src?.meta?.examMode ??
    src?.exam?.examMode ??
    ""
  )
    .trim()
    .toLowerCase();
/* ---------- audience helpers (section + branch) ---------- */
const same = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
const matchesSection = (item, section) => {
  if (!section) return true;
  const s = String(section).trim();
  if (Array.isArray(item.sections) && item.sections.length) {
    return item.sections.some((x) => same(x, s)) || item.sections.some((x) => same(x, "all"));
  }
  if (item.section) return same(item.section, s) || same(item.section, "all");
  return true;
};
const matchesBranch = (item, branch) => {
  const b = String(branch || "").trim();
  if (!b) return true;
  if (Array.isArray(item.branches) && item.branches.length) {
    return item.branches.some((x) => same(x, b)) || item.branches.some((x) => same(x, "all"));
  }
  if (item.branch) return same(item.branch, b) || same(item.branch, "all");
  return true;
};
const matchesAudience = (item, ctx) => matchesSection(item, ctx?.section) && matchesBranch(item, ctx?.branch);

/* ---------------- evaluation helpers ---------------- */
const normStr = (s) => String(s || "").trim().toLowerCase();
const getMarks = (q) => Number(q.marks ?? q.maxMarks ?? 1) || 1;
const getNeg = (q) => Math.max(0, Number(q.negativeMarks ?? q.negative ?? q.neg ?? 0) || 0);
const getAnsVal = (ans) => (ans && typeof ans === "object" && "value" in ans ? ans.value : ans);

const readAttemptScore = (a = {}) =>
  Number(a.score ?? a.evaluatedScore ?? a.marksObtained ?? a.obtained ?? 0) || 0;

const readAttemptTotal = (a = {}) =>
  Number(a.totalMarks ?? a.maxScore ?? a.fullMarks ?? a.total ?? a.outOf ?? 0) || 0;

const readExamTotal = (ex = {}) => {
  let t = Number(ex.totalMarks ?? ex.maxScore ?? ex.fullMarks ?? ex.outOf ?? 0) || 0;
  if (!t) {
    const q = Number(ex.totalQuestions ?? ex.questions ?? ex.qCount ?? 0) || 0;
    const per = Number(ex.marksPerCorrect ?? ex.marksPerQ ?? ex.marks ?? 1) || 1;
    if (q) t = q * per;
  }
  return t;
};

/* ---------------- exam meta helpers (for titles/types) ---------------- */
const prettyTitle = (val = "") =>
  String(val)
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    const s = v == null ? "" : String(v);
    if (s.trim()) return s.trim();
  }
  return "";
};
const get = (obj, path) => {
  try { return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj); }
  catch { return undefined; }
};

// Admin-defined mode/category
const getCategoryMeta = (obj) => {
  if (!obj) return null;

  // Try lots of label fields
  const labelRaw =
    obj?.testTypeLabel ??
    obj?.examTypeLabel ??       // sometimes saved like this
    obj?.examModeLabel ??
    obj?.templateModeLabel ??
    obj?.categoryLabel ??
    obj?.modeLabel ??
    obj?.meta?.testTypeLabel ?? // nested variants
    obj?.meta?.categoryLabel ??
    null;

  // Try lots of key-ish fields
  let keyRaw =
    obj?.testType ??
    obj?.test_type ??
    obj?.examTypeKey ??
    obj?.examModeKey ??
    obj?.examCategoryKey ??
    obj?.templateModeKey ??
    obj?.templateMode ??
    obj?.examCategory ??
    obj?.category ??
    obj?.meta?.testType ??
    obj?.meta?.category ??
    null;

  if (!keyRaw && labelRaw) {
    keyRaw = String(labelRaw).trim().toLowerCase().replace(/\s+/g, "_");
  }

  // Don’t confuse run-window with type
  const run = String(obj?.examMode || "").toLowerCase();
  if ((keyRaw === "live" || keyRaw === "mock") && !labelRaw && (run === "live" || run === "mock")) {
    keyRaw = null;
  }

  const value = keyRaw ? String(keyRaw).toLowerCase() : null;
  const label = labelRaw || (value ? prettyTitle(value) : null);

  return value && label ? { value, label } : null;
};


// Resolve title/subject/type using many possible keys (exam & attempt)
const resolveExamMeta = (ex, at) => {
  const title = firstNonEmpty(
    ex?.name, ex?.title, ex?.examName, ex?.paperName,
    get(ex, "meta.name"), get(ex, "basic.name"),
    at?.examName, at?.examTitle, at?.name, at?.title,
    get(at, "exam.name"), get(at, "examInfo.name"), get(at, "paper.title")
  ) || "Exam";

  const subject = firstNonEmpty(
    ex?.subject, get(ex, "meta.subject"),
    at?.subject, get(at, "exam.subject")
  );

  const rawType = firstNonEmpty(
    ex?.examType, ex?.type, ex?.testTypeLabel, ex?.examModeLabel, ex?.templateModeLabel, ex?.categoryLabel,
    at?.examType, at?.type, get(at, "exam.type")
  );

  return { title, subject, type: rawType ? prettyTitle(rawType) : "" };
};

/** local evaluation (objective Qs + case-based) */
const evaluateAttemptLocally = (attempt, questions) => {
  if (!attempt || !questions?.length) {
    return { resultsByQuestion: {}, answeredCount: 0, correctCount: 0, score: readAttemptScore(attempt), total: readAttemptTotal(attempt) };
  }
  const answers = attempt.answers || {};
  let total = 0, score = 0, answered = 0, correct = 0;
  const rbq = {};
  const toKey = (v) =>
    Number.isInteger(v) ? String.fromCharCode(65 + Number(v)) : String(v ?? "").toUpperCase();

  for (const q of questions) {
    const qt = normStr(q.questionType);
    const marks = getMarks(q);
    const neg = getNeg(q);
    total += marks;

    if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
      total -= marks;
      for (const sq of q.caseSubQs) {
        const m = Number(sq.marks ?? q.marks ?? 1) || 1;
        const n = Number(sq.negativeMarks ?? q.negativeMarks ?? 0) || 0;
        total += m;
        const a = getAnsVal(answers[sq.id]);
        const chosen = toKey(a);
        const ok = chosen && String(sq.correctKey || "").toUpperCase() === chosen;
        if (String(a ?? "").trim() !== "") answered += 1;
        if (ok) { score += m; correct += 1; rbq[sq.id] = { correct: true, marks: m, evaluated: true, parentId: q.id }; }
        else if (String(a ?? "").trim() !== "") { score -= n; rbq[sq.id] = { correct: false, marks: -n, evaluated: true, parentId: q.id }; }
        else rbq[sq.id] = { correct: false, marks: 0, evaluated: false, parentId: q.id };
      }
      continue;
    }

    const a = getAnsVal(answers[q.id]);
    const answeredNow = String(a ?? "").trim() !== "";
    if (answeredNow) answered += 1;

    if (qt === "mcq" || qt === "mcq_descriptive") {
      const chosen = toKey(a);
      const correctKey = (q.options || []).find((o) => o.isCorrect)?.key ?? "";
      if (chosen && String(correctKey).toUpperCase() === chosen) { score += marks; correct += 1; rbq[q.id] = { correct: true, marks, evaluated: true }; }
      else if (answeredNow) { score -= neg; rbq[q.id] = { correct: false, marks: -neg, evaluated: true }; }
      else rbq[q.id] = { correct: false, marks: 0, evaluated: false };
      continue;
    }

    if (qt === "multi_correct") {
      const chosen = Array.isArray(a) ? a.map(String) : String(a || "").split(",").map((s) => s.trim());
      const correctKeys = (q.options || []).filter((o) => o.isCorrect).map((o) => String(o.key));
      const ok = chosen.length && chosen.length === correctKeys.length && chosen.every((k) => correctKeys.map((x)=>x.toUpperCase()).includes(k.toUpperCase()));
      if (ok) { score += marks; correct += 1; rbq[q.id] = { correct: true, marks, evaluated: true }; }
      else if (answeredNow) { score -= neg; rbq[q.id] = { correct: false, marks: -neg, evaluated: true }; }
      else rbq[q.id] = { correct: false, marks: 0, evaluated: false };
      continue;
    }

    if (qt === "true_false" || qt === "tf") {
      const ok = String(a) === String(q.trueFalseAnswer || "True");
      if (ok) { score += marks; correct += 1; rbq[q.id] = { correct: true, marks, evaluated: true }; }
      else if (answeredNow) { score -= neg; rbq[q.id] = { correct: false, marks: -neg, evaluated: true }; }
      else rbq[q.id] = { correct: false, marks: 0, evaluated: false };
      continue;
    }

    if (qt === "match") {
      const ok = Number(a) === Number(q.correctOptionIndex);
      if (ok) { score += marks; correct += 1; rbq[q.id] = { correct: true, marks, evaluated: true }; }
      else if (answeredNow) { score -= neg; rbq[q.id] = { correct: false, marks: -neg, evaluated: true }; }
      else rbq[q.id] = { correct: false, marks: 0, evaluated: false };
      continue;
    }

    if (qt === "assertion_reason" || qt === "ar") {
      const ok = String(a).toUpperCase() === String(q.arAnswer || "A").toUpperCase();
      if (ok) { score += marks; correct += 1; rbq[q.id] = { correct: true, marks, evaluated: true }; }
      else if (answeredNow) { score -= neg; rbq[q.id] = { correct: false, marks: -neg, evaluated: true }; }
      else rbq[q.id] = { correct: false, marks: 0, evaluated: false };
      continue;
    }

    // descriptive: ignore in local grading
    rbq[q.id] = { correct: false, marks: 0, evaluated: false };
  }
  return { resultsByQuestion: rbq, answeredCount: answered, correctCount: correct, score, total };
};

/* ---------------- tiny UI helpers ---------------- */
const normSubj = (s) => {
  const x = String(s || "").trim();
  if (!x) return "";
  const low = x.toLowerCase();
  if (low.includes("math")) return "Math";
  if (low.includes("phy")) return "Physics";
  if (low.includes("chem")) return "Chemistry";
  if (low.includes("bio")) return "Biology";
  if (low.includes("sci")) return "Science";
  if (low.includes("social") || low.includes("sst")) return "Social";
  if (low.includes("eng")) return "English";
  return x.replace(/\b\w/g, (c) => c.toUpperCase());
};
const getExamTopics = (exam) => {
  if (!exam) return [];
  if (Array.isArray(exam.topics)) return exam.topics.map((t) => String(t || "").trim()).filter(Boolean);
  if (exam.topic) return [String(exam.topic).trim()].filter(Boolean);
  if (exam.chapterTopic) return [String(exam.chapterTopic).trim()].filter(Boolean);
  return [];
};
const Sparkline = ({ data = [], height = 28, strokeWidth = 2 }) => {
  const w = Math.max(60, Math.min(260, Math.max(1, data.length) * 16));
  if (!data.length) return <div className="text-muted small">No data</div>;
  const max = Math.max(...data, 1);
  const stepX = w / Math.max(1, data.length - 1);
  const pts = data.map((v, i) => {
    const x = i * stepX;
    const y = height - (height * v) / max;
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={height}>
      <polyline fill="none" stroke="currentColor" strokeWidth={strokeWidth} points={pts.join(" ")} />
    </svg>
  );
};
// Tri-state + size controls
// Simple, no TypeScript types
// Flex + wrap, fixed-size squares
const HeatMini = ({
  days = [],       // [{ date: Date, present: true|false|null }]
  size = 20,       // square size in px (make this bigger/smaller)
  gap = 6,         // spacing between squares (px)
  radius = 5       // rounded corners (px)
}) => {
  const color = (v) =>
    v === true  ? "rgba(25,135,84,.9)" :    // present
    v === false ? "rgba(220,53,69,.85)" :   // absent
                  "#e9ecef";                // no record

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        gap,                 // works in all modern browsers
        alignItems: "center"
      }}
    >
      {days.map((d, i) => {
        const v = d?.present;
        return (
          <div
            key={i}
            title={
              d?.date
                ? `${dayjs(d.date).format("DD MMM")} • ${
                    v === true ? "Present" : v === false ? "Absent" : "No record"
                  }`
                : ""
            }
            style={{
              width: size,
              height: size,
              borderRadius: radius,
              background: color(v),
              flex: `0 0 ${size}px`
            }}
          />
        );
      })}
    </div>
  );
};


const attemptWhen = (a = {}) => toJS(a.submittedAt || a.completedAt || a.finishedAt || a.endedAt || a.updatedAt || a.createdAt);

/* ---------------- questions cache (module-scope) ---------------- */
const QS_CACHE = new Map();
const getQsForTemplate = async (schoolId, templateId) => {
  if (!templateId) return [];
  if (QS_CACHE.has(templateId)) return QS_CACHE.get(templateId);
  const qCol = collection(db, "schools", schoolId, "questions");
  const qSnap = await getDocs(query(qCol, where("templateId", "==", templateId), limit(800)));
  const arr = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  QS_CACHE.set(templateId, arr);
  return arr;
};

/* ===================================================================== */

export default function ProgressCard() {
  const auth = getAuth();
  const uid = auth.currentUser?.uid || "";
  const [loading, setLoading] = useState(true);

  // user context
  const [userCtx, setUserCtx] = useState(null);
  const schoolId = userCtx?.schoolId || "";
  const studentId = userCtx?.studentId || "";

  // student profile
  const [profile, setProfile] = useState(null);

  // modules
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [attendance, setAttendance] = useState([]);

  // filter (admin-defined exam mode/category)
  const [setFMode] = useState("all");
  const [fTestType, setFTestType] = useState("all");
   const [fRunMode, setFRunMode]   = useState("all");


  // toasts
  const [toast, setToast] = useState({ show: false, title: "", body: "" });
  const showToast = (title, body) => setToast({ show: true, title, body });

  // status
  const statusEffective = (() => {
    const p = String(profile?.status || "").trim().toLowerCase();
    const u = String(userCtx?.status || "").trim().toLowerCase();
    return p || u || "inactive";
  })();
  const isActive = statusEffective === "active";
  const loadingProfile = !userCtx || ((schoolId && studentId) ? loading : false);

  /* ------ fast exam indexes (by id and by templateId) ------ */
 const exMap = useMemo(() => {
  const m = new Map();
  exams.forEach((e) => m.set(e.id, e));
  return m;
}, [exams]);
// Unique daily attendance (latest entry per day), newest first
const attendanceDaily = useMemo(() => {
  const map = new Map(); // YYYY-MM-DD -> row
  attendance.forEach((x) => {
    const dt = toJS(x.date || x.markedAt || x.createdAt);
    if (!dt) return;
    const key = dayjs(dt).format("YYYY-MM-DD");
    const prev = map.get(key);
    if (!prev || +dt > +(toJS(prev.date || prev.markedAt || prev.createdAt))) {
      map.set(key, x);
    }
  });
  return Array.from(map.values()).sort(
    (a, b) =>
      +(toJS(b.date || b.markedAt || b.createdAt)) -
      +(toJS(a.date || a.markedAt || a.createdAt))
  );
}, [attendance]);

const exByTemplate = useMemo(() => {
  const m = new Map();
  exams.forEach((e) => {
    const t = String(e.templateId || "").trim();
    if (t) m.set(t, e);
  });
  return m;
}, [exams]);

const findExamForAttempt = useCallback((at) => {
  if (!at) return null;
  let ex = at?.examId ? exMap.get(at.examId) : undefined;
  if (!ex && at?.templateId) ex = exByTemplate.get(String(at.templateId).trim());
  if (!ex && at?._ex) ex = at._ex; // your fallback
  return ex || null;
}, [exMap, exByTemplate])
   const runModeOptions = useMemo(() => {
  const set = new Set();
  exams.forEach((e) => { const m = runModeOf(e); if (m) set.add(m); });
  attempts.forEach((a) => {
    const ex = findExamForAttempt(a);
    const m  = runModeOf(ex) || runModeOf(a);
    if (m) set.add(m);
  });
  const label = (v) => (v === "live" ? "Live window" : v === "mock" ? "Anytime (Mock)" : prettyTitle(v));
  return [{ value: "all", label: "All modes" }, ...Array.from(set).map((v) => ({ value: v, label: label(v) }))];
}, [exams, attempts, findExamForAttempt]);
const getTestTypeMeta = (src) => {
  if (!src) return null;
  const labelRaw =
    src?.testTypeLabel ||
    src?.examModeLabel ||
    src?.templateModeLabel ||
    src?.categoryLabel ||
    src?.modeLabel ||
    null;

  let keyRaw =
    src?.testType ||
    src?.test_type ||
    src?.examModeKey ||
    src?.examCategoryKey ||
    src?.templateModeKey ||
    src?.templateMode ||
    src?.category ||
    null;

  if (!keyRaw && labelRaw) keyRaw = String(labelRaw).trim().toLowerCase().replace(/\s+/g, "_");

  // don’t confuse run window with type
  const run = String(src?.examMode || "").toLowerCase();
  if ((keyRaw === "live" || keyRaw === "mock") && !labelRaw && (run === "live" || run === "mock")) keyRaw = null;

  const value = keyRaw ? String(keyRaw).toLowerCase() : null;
  const label = labelRaw || (value ? prettyTitle(value) : null);
  return value && label ? { value, label } : null;
};

  /* -------- load user context -------- */
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!uid) return;
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
          if (!ignore) setUserCtx({ id: snap.id, ...snap.data() });
        } else {
          showToast("Profile", "User document not found.");
        }
      } catch (e) {
        console.error(e);
        showToast("Error", "Failed to load user profile.");
      }
    })();
    return () => { ignore = true; };
  }, [uid]);

  /* -------- load student profile -------- */
  useEffect(() => {
    if (!schoolId || !studentId) return;
    let mounted = true;
    (async () => {
      try {
        const ref = doc(db, "schools", schoolId, "students", studentId);
        const snap = await getDoc(ref);
        if (mounted) setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [schoolId, studentId]);

  /* -------- attendance -------- */
  useEffect(() => {
    if (!schoolId || !userCtx?.className) return;
    const col = collection(db, "schools", schoolId, "attendance");
    const store = new Map();
    const unsubs = [];

    const push = (snap) => {
      const merged = uniqPushIntoMap(store, snap.docs);
      const arr = merged
        .filter((x) => {
          const clsOk = x.className ? x.className === userCtx.className : true;
          const secOk = matchesSection(x, userCtx.section);
          const brOk = matchesBranch(x, userCtx.branch);
          const stOk = x.studentId ? x.studentId === studentId : true;
          return clsOk && secOk && brOk && stOk;
        })
        .sort((a, b) => +toJS(b.date || b.markedAt || b.createdAt) - +toJS(a.date || a.markedAt || a.createdAt));
      setAttendance(arr);
    };
    const onErr = (e) => console.error(e);

    if (userCtx.section) {
      unsubs.push(onSnapshot(
        query(col, where("className", "==", userCtx.className), where("sections", "array-contains", userCtx.section)),
        push, onErr
      ));
    }
    unsubs.push(onSnapshot(
      userCtx.section
        ? query(col, where("className", "==", userCtx.className), where("section", "in", [userCtx.section, "all"]))
        : query(col, where("className", "==", userCtx.className)),
      push, onErr
    ));
    unsubs.push(onSnapshot(query(col, where("studentId", "==", studentId)), push, onErr));

    return () => unsubs.forEach((u) => u && u());
  }, [schoolId, userCtx?.className, userCtx?.section, userCtx?.branch, studentId]);

  /* -------- assignments + submissions -------- */
  useEffect(() => {
    if (!schoolId || !userCtx?.className) return;
    const aCol = collection(db, "schools", schoolId, "assignments");

    const results = new Map();
    const onBatch = (snap) => {
      const got = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      got.forEach((g) => results.set(g.id, g));
      const arr = Array.from(results.values())
        .filter((x) => matchesAudience(x, userCtx))
        .sort((a, b) => +toJS(b.dueAt || b.createdAt) - +toJS(a.dueAt || a.createdAt));
      setAssignments(arr);
    };
    const onErr = (e) => console.error(e);
    const unsubs = [];

    if (userCtx.section) {
      unsubs.push(onSnapshot(
        query(aCol, where("className", "==", userCtx.className), where("sections", "array-contains", userCtx.section)),
        onBatch, onErr
      ));
      unsubs.push(onSnapshot(
        query(aCol, where("className", "==", userCtx.className), where("section", "in", [userCtx.section, "all"])),
        onBatch, onErr
      ));
    } else {
      unsubs.push(onSnapshot(query(aCol, where("className", "==", userCtx.className)), onBatch, onErr));
    }

    (async () => {
      try {
        const qs = await getDocs(query(aCol, where("className", "==", userCtx.className)));
        const reads = qs.docs.slice(0, 60).map(async (ad) => {
          const sref = doc(db, "schools", schoolId, "assignments", ad.id, "submissions", uid);
          const sdoc = await getDoc(sref);
          return sdoc.exists() ? { assignmentId: ad.id, id: sdoc.id, ...sdoc.data() } : null;
        });
        const list = (await Promise.all(reads)).filter(Boolean);
        setSubmissions(list);
      } catch (e) {
        console.error(e);
      }
    })();

    return () => unsubs.forEach((u) => u && u());
  }, [schoolId, userCtx?.className, userCtx?.section, userCtx?.branch, uid]);

  /* -------- exams + attempts -------- */
  useEffect(() => {
    if (!schoolId || !uid) return;

    const { className, section, branch } = userCtx || {};
    const eCol = collection(db, "schools", schoolId, "exams");

    // live exams (audience)
    const results = new Map();
    const onBatch = (snap) => {
      const got = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      got.forEach((g) => results.set(g.id, g));
      const arr = Array.from(results.values())
        .filter((x) => matchesAudience(x, { className, section, branch }))
        .sort((a, b) => +toJS(b.scheduledAt || b.createdAt) - +toJS(a.scheduledAt || a.createdAt));
      setExams(arr);
    };
    const onErr = (e) => console.error(e);
    const unsubs = [];

    if (className && section) {
      unsubs.push(onSnapshot(
        query(eCol, where("className", "==", className), where("sections", "array-contains", section)),
        onBatch, onErr
      ));
      unsubs.push(onSnapshot(
        query(eCol, where("className", "==", className), where("section", "in", [section, "all"])),
        onBatch, onErr
      ));
    } else if (className) {
      unsubs.push(onSnapshot(query(eCol, where("className", "==", className)), onBatch, onErr));
    } else {
      unsubs.push(onSnapshot(eCol, onBatch, onErr));
    }

    // enrich attempts with percent (using local evaluation when needed)
    const enrichAttempts = async (rows) => {
      if (!rows.length) return rows;

      // exam cache from snapshot
      const examMap = new Map(Array.from(results.values()).map((e) => [e.id, e]));

      return Promise.all(rows.map(async (a) => {
        let score = readAttemptScore(a);
        let total = readAttemptTotal(a);

        let ex = examMap.get(a.examId);
        if (!ex) {
          try {
            const snap = await getDoc(doc(db, "schools", schoolId, "exams", a.examId));
            if (snap.exists()) ex = { id: snap.id, ...snap.data() };
          } catch {}
        }
        if (!total && ex) total = readExamTotal(ex);

        let pctVal = total > 0 ? (100 * score) / total : null;

        if ((pctVal == null || !Number.isFinite(pctVal)) && a.answers && ex?.templateId) {
          try {
            const qs = await getQsForTemplate(schoolId, ex.templateId);
            const ev = evaluateAttemptLocally(a, qs);
            if (Number.isFinite(ev.score)) score = ev.score;
            if (Number.isFinite(ev.total) && ev.total > 0) total = ev.total;
            pctVal = total > 0 ? (100 * score) / total : null;
          } catch (e) {
            console.error("local evaluate failed:", e);
          }
        }

        const pctFinal =
          pctVal != null && Number.isFinite(pctVal)
            ? Math.max(0, Math.min(100, Math.round(pctVal)))
            : (() => {
                const p = a.percent ?? a.percentage ?? a.scorePercent ?? a.scorePct ?? a.pct ?? null;
                const pn = Number(p);
                return Number.isFinite(pn) ? Math.max(0, Math.min(100, Math.round(pn))) : null;
              })();

        return {
          ...a,
          _score: Number.isFinite(score) ? score : 0,
          _total: total || null,
          _pct: pctFinal,
          _when: attemptWhen(a),
           _ex: ex || null,
        };
      }));
    };

    // attempts: collectionGroup → fallback per-exam
    const fetchAttemptsSmart = async () => {
      try {
        const cgQ = query(collectionGroup(db, "attempts"), where("uid", "==", uid));
        const aSnap = await getDocs(cgQ);
        const raw = aSnap.docs.map((d) => {
          const examRef = d.ref.parent.parent; // schools/{sid}/exams/{eid}
          if (!examRef) return null;
          const parts = examRef.path.split("/");
          const sid = parts[1];
          const eid = parts[3];
          if (sid !== schoolId) return null;
          const data = d.data();
          return { examId: eid, id: d.id, uid: data.uid ?? uid, ...data };
        }).filter(Boolean);

        const enriched = await enrichAttempts(raw);
        if (enriched.length) { setAttempts(enriched); return; }
      } catch (e) {
        if (!(e?.code === "failed-precondition" || /index/i.test(e?.message || ""))) {
          console.error(e);
        }
        // fall through
      }

      try {
        const exSnap = await getDocs(eCol); // ids only
        const examIds = exSnap.docs.map((d) => d.id);
        const reads = await Promise.all(
          examIds.slice(0, 200).map(async (eid) => {
            const ref1 = doc(db, "schools", schoolId, "exams", eid, "attempts", uid);
            const d1 = await getDoc(ref1);
            if (d1.exists()) return { examId: eid, id: d1.id, ...d1.data() };
            const alt = await getDocs(query(collection(db, "schools", schoolId, "exams", eid, "attempts"), where("uid", "==", uid)));
            if (!alt.empty) { const d0 = alt.docs[0]; return { examId: eid, id: d0.id, ...d0.data() }; }
            return null;
          })
        );
        const raw = reads.filter(Boolean);
        const enriched = await enrichAttempts(raw);
        setAttempts(enriched);
      } catch (e) {
        console.error(e);
      }
    };

    fetchAttemptsSmart();
    return () => unsubs.forEach((u) => u && u());
  }, [schoolId, uid, userCtx?.className, userCtx?.section, userCtx?.branch]);

  /* ---------- Mode options (from exams present) ---------- */
const modeOptions = useMemo(() => {
  const map = new Map();

  // from live/published exams
  exams.forEach((e) => {
    const meta = getTestTypeMeta(e);
    if (meta && !map.has(meta.value)) map.set(meta.value, meta.label);
  });

  // from attempts (fallback to attempt fields, or the exam backing an attempt)
  attempts.forEach((a) => {
    const ex = findExamForAttempt(a);
    const meta = getTestTypeMeta(ex) || getTestTypeMeta(a);
    if (meta && !map.has(meta.value)) map.set(meta.value, meta.label);
  });

  const arr = Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  arr.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  return [{ value: "all", label: "All test types" }, ...arr];
}, [exams, attempts,findExamForAttempt]);


// testTypeOptions
const testTypeOptions = useMemo(() => {
  const map = new Map();
  exams.forEach((e) => {
    const meta = getTestTypeMeta(e);
    if (meta && !map.has(meta.value)) map.set(meta.value, meta.label);
  });
  attempts.forEach((a) => {
    const ex = findExamForAttempt(a);             // uses callback
    const meta = getTestTypeMeta(ex) || getTestTypeMeta(a);
    if (meta && !map.has(meta.value)) map.set(meta.value, meta.label);
  });
  const arr = Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  arr.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  return [{ value: "all", label: "All test types" }, ...arr];
}, [exams, attempts, findExamForAttempt]);         // ✅ include it




  /* -------------------- KPIs & computed views -------------------- */
const kpis = useMemo(() => {
  // ---------- assignments ----------
  const totalAssignments = assignments.length;
  const myCompleted = submissions.filter(
    (s) => (s.status || "").toLowerCase() === "completed"
  ).length;
  const myPending = Math.max(0, totalAssignments - myCompleted);

  // ---------- attendance (dedupe, tri-state, this-month %) ----------
  const daysRaw = attendance
    .map((x) => {
      const dt = toJS(x.date || x.markedAt || x.createdAt) || new Date();
      const status = String(x.status || "").toLowerCase();

      // present: true | false | null (null = no record)
      let present = null;
      if (x.present != null) {
        present = !!x.present;
      } else if (x.isPresent != null) {
        present = !!x.isPresent;
      } else if (status) {
        // only if a status string actually exists
        present = status === "present";
      }

      return { date: dt, present };
    })
    .filter((x) => x.date);

  // de-dupe by calendar day (keep the latest entry for that day)
  const byDay = new Map();
  daysRaw.forEach((d) => {
    const key = dayjs(d.date).format("YYYY-MM-DD");
    const prev = byDay.get(key);
    if (!prev || +d.date > +prev.date) byDay.set(key, d);
  });

  // build current month as tri-state days
  const today = dayjs();
  const mStart = today.startOf("month");
  const daysInM = mStart.daysInMonth();

  const monthDays = Array.from({ length: daysInM }, (_, i) => {
    const key = mStart.date(i + 1).format("YYYY-MM-DD");
    const slotDate = new Date(key);

    if (dayjs(key).isAfter(today, "day")) {
      // future → null (grey)
      return { date: slotDate, present: null };
    }

    const rec = byDay.get(key);
    if (rec) return { date: slotDate, present: rec.present };

    // past but no record → null (grey, and don't count in %)
    return { date: slotDate, present: null };
  });

  // this month % from *recorded* past days only (ignore null & future)
  const recorded = monthDays.filter((d) => d.present !== null);
  const presentCount = recorded.filter((d) => d.present === true).length;
  const attRate = recorded.length
    ? Math.round((100 * presentCount) / recorded.length)
    : 0;

  // ---------- attempts filter by selected test type ----------
  const attemptsFiltered = attempts.filter((a) => {
    // test type / category (admin-defined)
    if (fTestType !== "all") {
      const ex = findExamForAttempt(a);
      const mEx = getTestTypeMeta(ex);
      const mAt = getTestTypeMeta(a);
      if (!((mEx && mEx.value === fTestType) || (mAt && mAt.value === fTestType))) return false;
    }
    // run mode (live / mock)
    if (fRunMode !== "all") {
      const ex = findExamForAttempt(a);
      const rm = runModeOf(ex) || runModeOf(a);
      if (rm !== fRunMode) return false;
    }
    return true;
 });

  // ---------- normalize attempts ----------
  const attemptsNorm = attemptsFiltered.map((a) => {
    let score = readAttemptScore(a);
    let total = readAttemptTotal(a);
    if (!total) {
      const ex = exams.find((e) => e.id === a.examId);
      if (ex) total = readExamTotal(ex);
    }
    let pctVal = null;
    if (total > 0) {
      pctVal = Math.max(0, Math.min(100, (100 * score) / total));
    } else {
      const p =
        a.percent ??
        a.percentage ??
        a.scorePercent ??
        a.scorePct ??
        a.pct ??
        null;
      const pn = Number(p);
      if (Number.isFinite(pn)) pctVal = Math.max(0, Math.min(100, pn));
    }
    return {
      ...a,
      _score: Number.isFinite(score) ? score : 0,
      _total: total || null,
      _pct: pctVal,
      _when: attemptWhen(a),
    };
  });

  // ---------- exam averages & charts ----------
  const graded = attemptsNorm.filter((a) => a._pct != null);
  const sumPct = graded.reduce((s, a) => s + a._pct, 0);
  const avgPct = graded.length ? Math.round(sumPct / graded.length) : 0;

  const scoreSeries = graded
    .slice()
    .sort((a, b) => +a._when - +b._when)
    .map((a) => Math.round(a._pct));

  const assignSeries = assignments
    .slice()
    .sort(
      (a, b) =>
        +toJS(a.dueAt || a.createdAt) - +toJS(b.dueAt || b.createdAt)
    )
    .map((a) =>
      (submissions.find((s) => s.assignmentId === a.id)?.status || "")
        .toLowerCase() === "completed"
        ? 100
        : 0
    );

  // ---------- subject aggregates ----------
  const subjAgg = new Map();
  const pushPct = (map, key, val) => {
    if (val == null || !Number.isFinite(val)) return;
    const arr = map.get(key) || [];
    arr.push(Math.max(0, Math.min(100, val)));
    map.set(key, arr);
  };

  graded.forEach((a) => {
    const ex = exams.find((e) => e.id === a.examId);
    const fallbackSubject = normSubj(ex?.subject || a.subject || "General");
    const sb = a?.analysis?.subjectBreakdown;
    if (Array.isArray(sb) && sb.length) {
      sb.forEach((row) => {
        const subject = normSubj(row.subject || fallbackSubject || "General");
        let pv = null;
        if (row.marks && Number(row.marks.total) > 0) {
          pv =
            (100 * Number(row.marks.awarded || 0)) /
            Number(row.marks.total || 0);
        } else if (Number(row.questions) > 0) {
          pv = (100 * Number(row.correct || 0)) / Number(row.questions || 0);
        }
        pushPct(subjAgg, subject, pv);
      });
    } else if (a._pct != null) {
      pushPct(subjAgg, fallbackSubject, a._pct);
    }
  });

  const subjectPct = Object.fromEntries(
    Array.from(subjAgg.entries()).map(([k, arr]) => [
      k,
      Math.round(arr.reduce((s, x) => s + x, 0) / arr.length),
    ])
  );

  // ---------- topic aggregates ----------
  const topicAgg = new Map();
  graded.forEach((a) => {
    const ex = exams.find((e) => e.id === a.examId);
    const tb = a?.analysis?.topicBreakdown;
    if (Array.isArray(tb) && tb.length) {
      tb.forEach((row) => {
        const topic = String(row.topic || "").trim() || "General";
        let pv = null;
        if (row.marks && Number(row.marks.total) > 0) {
          pv =
            (100 * Number(row.marks.awarded || 0)) /
            Number(row.marks.total || 0);
        } else if (Number(row.questions) > 0) {
          pv = (100 * Number(row.correct || 0)) / Number(row.questions || 0);
        }
        pushPct(topicAgg, topic, pv);
      });
      return;
    }
    const byTopic = a?.stats?.byTopic || a?.byTopic || null;
    if (byTopic && Object.keys(byTopic).length) {
      Object.entries(byTopic).forEach(([topic, row]) => {
        const tTot = Number(row.total || row.questions || 0);
        const tRight = Number(row.correct || row.right || 0);
        const pv = tTot ? (100 * tRight) / tTot : null;
        pushPct(topicAgg, topic, pv);
      });
    } else {
      const topics = getExamTopics(ex);
      if (topics.length && a._pct != null) {
        topics.forEach((tp) => pushPct(topicAgg, tp, a._pct));
      }
    }
  });

  const topicPct = Object.fromEntries(
    Array.from(topicAgg.entries()).map(([k, arr]) => [
      k,
      Math.round(arr.reduce((s, x) => s + x, 0) / arr.length),
    ])
  );

  const subjSorted = Object.entries(subjectPct).sort((a, b) => b[1] - a[1]);
  const topicSorted = Object.entries(topicPct).sort((a, b) => b[1] - a[1]);
  const peakSubjects = subjSorted.slice(0, 3);
  const weakSubjects = subjSorted.slice(-3);
  const peakTopics = topicSorted.slice(0, 3);
  const weakTopics = topicSorted.slice(-3);

  return {
    totalAssignments,
    myCompleted,
    myPending,
    avgPct,
    attRate,      // this month only, from recorded days
    scoreSeries,
    assignSeries,
    monthDays,    // feed this to HeatMini
    subjectPct,
    topicPct,
    peakSubjects,
    weakSubjects,
    peakTopics,
    weakTopics,
    attemptsNorm,
  };
}, [assignments, submissions, attempts, attendance, exams, fTestType,fRunMode, findExamForAttempt]);




  if (!uid) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body><div className="text-danger">Not signed in.</div></Card.Body>
      </Card>
    );
  }

  const assignmentPct = pct(kpis.myCompleted, Math.max(1, kpis.totalAssignments));

  return (
    <div className="progress-shell position-relative">
      {/* background pattern layers */}
      <div className="bg-grid" aria-hidden />
      <div className="bg-aurora" aria-hidden />

      {!loadingProfile && !isActive ? (
        <Alert variant="warning" className="mb-3">
          Your student account is currently <b>inactive</b>. Please contact your school
          management to activate your account to access your progress card.
        </Alert>
      ) : null}

      <Row className="g-3">
        {/* Hero: identity / actions */}
        <Col xs={12}>
          <Card className="card-bento hero">
            <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="avatar-ring">
                  <div className="avatar">
                    <i className="bi bi-person-fill" />
                  </div>
                </div>
                <div>
                  <div className="fw-semibold fs-5 d-flex align-items-center gap-2">
                    {safeTxt(profile?.name || userCtx?.displayName || "Student")}
                    <Badge bg={isActive ? "success" : "secondary"}>{safeTxt(statusEffective)}</Badge>
                  </div>
                  <div className="text-muted small">
                    Class {safeTxt(userCtx?.className)}
                    {" • "}Section {safeTxt(userCtx?.section)}
                    {userCtx?.branch ? <>{" • "}Branch {safeTxt(userCtx.branch)}</> : null}
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={() => window.print()}>
                  <i className="bi bi-printer me-1" /> Print
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    try {
                      const rows = [
                        ["Section", "Title", "Key", "Value"],
                        ["Profile", "Name", "", safeTxt(profile?.name || userCtx?.displayName || "Student")],
                        ["Profile", "Class", "", safeTxt(userCtx?.className)],
                        ["Profile", "Section", "", safeTxt(userCtx?.section)],
                        ["Profile", "Branch", "", safeTxt(userCtx?.branch)],
                        ["KPIs", "Assignments Completed", "", kpis.myCompleted],
                        ["KPIs", "Assignments Pending", "", kpis.myPending],
                        ["KPIs", "Average Exam %", "", kpis.avgPct],
["KPIs", `Attendance % (${dayjs().format("MMM")})`, "", kpis.attRate],
                      ];
                      Object.entries(kpis.subjectPct || {}).forEach(([s, v]) => rows.push(["Subjects", s, "avg%", v]));
                      Object.entries(kpis.topicPct || {}).forEach(([s, v]) => rows.push(["Topics", s, "avg%", v]));
                      const blob = new Blob([rows.map(r => r.map(String).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `progress_${uid}_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      console.error(e);
                      setToast({ show: true, title: "Export", body: "Failed to export CSV." });
                    }
                  }}>
                  <i className="bi bi-download me-1" /> Export CSV
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* KPI bento row */}
        <Col md={3} sm={6} xs={12}>
          <Card className="card-bento h-100">
            <Card.Body>
              <div className="kpi-head">
                <span className="chip"><i className="bi bi-journal-check" /></span>
                <span>Assignments</span>
              </div>
              <div className="d-flex align-items-end justify-content-between mt-2">
                <div className="fs-3 fw-semibold">{kpis.myCompleted}/{kpis.totalAssignments}</div>
                <Badge bg={kpis.myPending ? "warning" : "success"}>
                  {kpis.myPending ? `${kpis.myPending} pending` : "All done"}
                </Badge>
              </div>
              <div className="mt-3">
                <ProgressBar
                  className="bar-glow"
                  variant={variantByPct(assignmentPct)}
                  now={assignmentPct}
                />
              </div>
              <div className="mt-2 text-muted small">Completion trend</div>
              <div className="text-primary"><Sparkline data={kpis.assignSeries} /></div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} xs={12}>
          <Card className="card-bento h-100">
            <Card.Body>
              <div className="kpi-head">
                <span className="chip"><i className="bi bi-graph-up-arrow" /></span>
                <span>Average Exam %</span>
              </div>

 

              <div className="fs-3 fw-semibold">
                {kpis.avgPct}<span className="fs-6">%</span>
              </div>
              <div className="mt-2">
                <ProgressBar
                  className="bar-glow"
                  variant={variantByPct(kpis.avgPct)}
                  now={kpis.avgPct}
                />
              </div>
              <div className="mt-2 text-muted small">Last results</div>
              <div className="text-primary"><Sparkline data={kpis.scoreSeries} /></div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} xs={12}>
          <Card className="card-bento h-100">
            <Card.Body>
              <div className="kpi-head">
                <span className="chip"><i className="bi bi-clipboard-check" /></span>
<span>Attendance (This Month)</span>              </div>
              <div className="d-flex align-items-end justify-content-between mt-2">
                <div className="fs-3 fw-semibold">{kpis.attRate}<span className="fs-6">%</span></div>
                <span className="text-muted small">Present / Absent</span>
              </div>
              <div className="mt-3"><HeatMini days={kpis.monthDays}/>

</div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} xs={12}>
          <Card className="card-bento h-100">
            <Card.Body>
              <div className="kpi-head">
                <span className="chip"><i className="bi bi-magic" /></span>
                <span>Quick Actions</span>
              </div>
              <ListGroup variant="flush" className="mt-2 list-clean">
                <ListGroup.Item className="px-0 d-flex justify-content-between align-items-center">
                  Download Progress CSV
                  <Button size="sm" variant="outline-primary">Download</Button>
                </ListGroup.Item>
                <ListGroup.Item className="px-0 d-flex justify-content-between align-items-center">
                  Print Progress
                  <Button size="sm" variant="outline-secondary" onClick={() => window.print()}>Print</Button>
                </ListGroup.Item>
                <ListGroup.Item className="px-0 text-muted small">
                  Tip: Keep your app online during exam windows to sync instantly.
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* Progress Overview */}
        <Col xs={12}>
          <Card className="card-bento">
            <Card.Header className="bento-header d-flex align-items-center justify-content-between">
              <div>
                <div className="fw-semibold">Progress Overview</div>
                <div className="small text-muted">Live view based on your school’s data</div>
              </div>
              {/* Secondary mode filter control */}
              <div className="d-none d-md-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-2">
   <span className="small text-muted">Type:</span>
   <Form.Select size="sm" value={fTestType} onChange={(e)=>setFTestType(e.target.value)} style={{maxWidth:220}}>
   {testTypeOptions.map((o)=>(<option key={o.value} value={o.value}>{o.label}</option>))}
 </Form.Select>
   <span className="small text-muted">Run:</span>
   <Form.Select size="sm" value={fRunMode} onChange={(e)=>setFRunMode(e.target.value)} style={{maxWidth:180}}>
     {runModeOptions.map((o)=>(<option key={o.value} value={o.value}>{o.label}</option>))}
   </Form.Select>
</div>
              </div>
            </Card.Header>

            <Card.Body>
              <Row className="g-3">
                {/* Assignments */}
                <Col md={6}>
                  <Card className="card-sub">
                    <Card.Header className="bento-subheader">
                      <span className="chip chip-sm"><i className="bi bi-journal-text" /></span>
                      <span className="fw-semibold">Assignments</span>
                    </Card.Header>

                    {/* Desktop/Tablet */}
                    <Card.Body className="scrollarea d-none d-sm-block">
                      {assignments.length === 0 ? (
                        <div className="text-muted">No assignments found.</div>
                      ) : (
                        <Table hover size="sm" responsive className="mb-0 table-slim">
                          <thead className="thead-sticky">
                            <tr><th>Title</th><th>Due</th><th>Status</th></tr>
                          </thead>
                          <tbody>
                            {assignments.map((a) => {
                              const mine = submissions.find((s) => s.assignmentId === a.id);
                              const st = (mine?.status || "pending").toLowerCase();
                              return (
                                <tr key={a.id}>
                                  <td className="text-truncate" style={{ maxWidth: 240 }}>{safeTxt(a.title || "Untitled")}</td>
                                  <td className="text-nowrap">{a.dueAt ? dayjs(toJS(a.dueAt)).format("DD MMM, HH:mm") : "—"}</td>
                                  <td><Badge bg={st === "completed" ? "success" : "warning"}>{st}</Badge></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>

                    {/* Mobile */}
                    <Card.Body className="scrollarea d-sm-none">
                      {assignments.length === 0 ? (
                        <div className="text-muted">No assignments found.</div>
                      ) : (
                        <ListGroup variant="flush">
                          {assignments.map((a) => {
                            const mine = submissions.find((s) => s.assignmentId === a.id);
                            const st = (mine?.status || "pending").toLowerCase();
                            return (
                              <ListGroup.Item key={a.id} className="px-0 d-flex justify-content-between align-items-start">
                                <div className="me-2">
                                  <div className="fw-semibold text-truncate" style={{ maxWidth: 220 }}>{safeTxt(a.title || "Untitled")}</div>
                                  <div className="small text-muted">{a.dueAt ? dayjs(toJS(a.dueAt)).format("DD MMM, HH:mm") : "—"}</div>
                                </div>
                                <Badge bg={st === "completed" ? "success" : "warning"}>{st}</Badge>
                              </ListGroup.Item>
                            );
                          })}
                        </ListGroup>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                {/* Recent Exams */}
                <Col md={6}>
                  <Card className="card-sub">
                    <Card.Header className="bento-subheader d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <span className="chip chip-sm"><i className="bi bi-trophy" /></span>
                        <span className="fw-semibold">Recent Exams</span>
                      </div>
                     <Form.Select
  size="sm"
  value={fTestType}
  onChange={(e) => setFTestType(e.target.value)}
  style={{ maxWidth: 220 }}
  aria-label="Filter by test type"
>
  {testTypeOptions.map((o) => (
    <option key={o.value} value={o.value}>{o.label}</option>
  ))}
</Form.Select>
                    </Card.Header>

                    {/* Desktop/Tablet */}
                    <Card.Body className="scrollarea d-none d-sm-block">
                      {!kpis.attemptsNorm?.length ? (
                        <div className="text-muted">No exam results yet.</div>
                      ) : (
                        <Table hover size="sm" responsive className="mb-0 table-slim">
                          <thead className="thead-sticky">
<tr><th>Exam</th><th>Type</th><th>Mode</th><th>When</th><th className="text-end">Score</th></tr>                     </thead>
                          <tbody>
                           {kpis.attemptsNorm
  .slice()
  .sort((a, b) => +b._when - +a._when)
.map((at) => {
  const ex   = findExamForAttempt(at);
  const meta = resolveExamMeta(ex, at);
 const when = at._when ? dayjs(at._when).format("DD MMM, HH:mm") : "—";
 const runMode = runModeOf(ex) || runModeOf(at) || null;
  // NEW: robust type label fallback chain
  
  const catFromExam    = getCategoryMeta(ex)?.label;
  const catFromAttempt = getCategoryMeta(at)?.label;
  const typeLabel = catFromExam || catFromAttempt || (meta.type || null);

  return (
    <tr key={`${at.id}-${at.examId || at.templateId || when}`}>
      <td className="text-truncate" style={{ maxWidth: 260 }}>
        <div className="fw-semibold">{meta.title}</div>
        {meta.subject ? (
          <div className="small text-muted text-truncate">{meta.subject}</div>
        ) : null}
      </td>

      <td className="text-nowrap">
        {typeLabel ? (
          <Badge bg="light" text="dark">{typeLabel}</Badge>
        ) : "—"}
      </td>
<td className="text-nowrap">
   {runMode ? (<Badge bg={runMode === "live" ? "success" : "secondary"}>
     {runMode === "live" ? "Live" : "Mock"}
   </Badge>) : "—"}
</td>
      <td className="text-nowrap">{when}</td>

     <td className="text-end">
  {at._pct != null ? (
    <Badge bg={variantByPct(Math.round(at._pct))}>
      {Math.round(at._pct)}%
    </Badge>
  ) : at._total ? (
    <span className="text-muted">
      {Math.round(at._score)} / {Math.round(at._total)}
    </span>
  ) : (
    <span className="text-muted">—</span>
  )}
</td>
    </tr>
  );
})}

                          </tbody>
                        </Table>
                      )}
                    </Card.Body>

                    {/* Mobile */}
                    <Card.Body className="scrollarea d-sm-none">
                      {!kpis.attemptsNorm?.length ? (
                        <div className="text-muted">No exam results yet.</div>
                      ) : (
                        <ListGroup variant="flush">
                         {kpis.attemptsNorm
  .slice()
  .sort((a, b) => +b._when - +a._when)
  .map((at) => {
    const ex   = findExamForAttempt(at);
    const meta = resolveExamMeta(ex, at);
    const cat  = getCategoryMeta(ex); // <-- define cat here too
    const when = at._when ? dayjs(at._when).format("DD MMM, HH:mm") : "—";
    const scPct = at._pct != null ? Math.round(at._pct) : null;
    const extra = [meta.subject].filter(Boolean).join(" • ");

    return (
      <ListGroup.Item
        key={`${at.id}-${at.examId || at.templateId || when}`}
        className="px-0 d-flex justify-content-between align-items-start"
      >
        <div className="me-2">
          <div className="fw-semibold text-truncate" style={{ maxWidth: 220 }}>
            {meta.title}
          </div>
        <div className="mb-1 d-flex gap-2 flex-wrap">
             {cat?.label ? <Badge bg="light" text="dark">{cat.label}</Badge> : null}
   {(() => { const rm = runModeOf(ex) || runModeOf(at); return rm ? (
     <Badge bg={rm === "live" ? "success" : "secondary"}>{rm === "live" ? "Live" : "Mock"}</Badge>
   ) : null; })()}

 </div>
          <div className="small text-muted text-truncate" style={{ maxWidth: 220 }}>
            {extra || "—"}
          </div>
          <div className="small text-muted">{when}</div>
        </div>
        <Badge bg={scPct != null ? variantByPct(scPct) : "secondary"}>
          {scPct != null ? `${scPct}%` : "—"}
        </Badge>
      </ListGroup.Item>
    );
  })}
                        </ListGroup>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Exams Analysis */}
        <Col xs={12}>
          <Card className="card-bento">
            <Card.Header className="bento-header d-flex align-items-center justify-content-between">
              <span className="fw-semibold">Exams Analysis — Subjects & Topics</span>
              <Form.Select
                size="sm"
                value={fTestType}
                onChange={(e) => setFMode(e.target.value)}
                aria-label="Filter by test type"
                style={{ maxWidth: 220 }}
              >
                {modeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col lg={6}>
                  <div className="fw-semibold mb-2">Subject-wise Performance</div>
                  {!Object.keys(kpis.subjectPct || {}).length ? (
                    <div className="text-muted">No subject data yet.</div>
                  ) : (
                    <Table hover size="sm" responsive className="mb-0 table-slim">
                      <thead className="thead-sticky">
                        <tr><th>Subject</th><th className="text-end">Avg %</th></tr>
                      </thead>
                      <tbody>
                        {Object.entries(kpis.subjectPct)
                          .sort((a,b)=>b[1]-a[1])
                          .map(([s,v], idx, arr) => {
                            const isBest = idx === 0;
                            const isWorst = idx === arr.length - 1;
                            return (
                              <tr key={s} className={isBest ? "table-success" : isWorst ? "table-danger" : ""}>
                                <td>{s}</td>
                                <td className="text-end"><Badge bg={variantByPct(v)}>{v}%</Badge></td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  )}
                </Col>

                <Col lg={6}>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="fw-semibold mb-1">Top Subjects</div>
                      {kpis.peakSubjects?.length ? (
                        <ul className="mb-3">
                          {kpis.peakSubjects.map(([name, pv]) => (
                            <li key={name}><Badge bg="success" className="me-2">{pv}%</Badge>{name}</li>
                          ))}
                        </ul>
                      ) : <div className="text-muted">—</div>}

                      <div className="fw-semibold mb-1">Top Topics</div>
                      {kpis.peakTopics?.length ? (
                        <ul className="mb-0">
                          {kpis.peakTopics.map(([name, pv]) => (
                            <li key={name}><Badge bg="success" className="me-2">{pv}%</Badge>{name}</li>
                          ))}
                        </ul>
                      ) : <div className="text-muted">—</div>}
                    </Col>

                    <Col md={6}>
                      <div className="fw-semibold mb-1">Weak Subjects</div>
                      {kpis.weakSubjects?.length ? (
                        <ul className="mb-3">
                          {kpis.weakSubjects.map(([name, pv]) => (
                            <li key={name}><Badge bg="danger" className="me-2">{pv}%</Badge>{name}</li>
                          ))}
                        </ul>
                      ) : <div className="text-muted">—</div>}

                      <div className="fw-semibold mb-1">Weak Topics</div>
                      {kpis.weakTopics?.length ? (
                        <ul className="mb-0">
                          {kpis.weakTopics.map(([name, pv]) => (
                            <li key={name}><Badge bg="danger" className="me-2">{pv}%</Badge>{name}</li>
                          ))}
                        </ul>
                      ) : <div className="text-muted">—</div>}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Attendance (scrollable recent) */}
        <Col xs={12}>
          <Card className="card-bento">
            <Card.Header className="bento-header"><span className="fw-semibold">Attendance (recent)</span></Card.Header>

            {/* Desktop/Tablet */}
            <Card.Body className="scrollarea d-none d-sm-block">
              {attendance.length === 0 ? (
                <div className="text-muted">No attendance records available.</div>
              ) : (
                <Table size="sm" responsive hover className="mb-0 table-slim">
                  <thead className="thead-sticky"><tr><th>Date</th><th>Status</th><th>Marked By</th></tr></thead>
                  <tbody>
                    {attendanceDaily.map((d) => {
                      const dt = toJS(d.date || d.markedAt || d.createdAt);
                      const ok = !!(d.present ?? d.isPresent ?? d.status === "present");
                      return (
                        <tr key={d.id}>
                          <td className="text-nowrap">{dt ? dayjs(dt).format("DD MMM, ddd") : "—"}</td>
                          <td><Badge bg={ok ? "success" : "danger"}>{ok ? "Present" : "Absent"}</Badge></td>
                          <td className="text-truncate" style={{ maxWidth: 280 }}>
                            {safeTxt(d.markedByName || d.teacherName || d.by || "—")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Mobile */}
            <Card.Body className="scrollarea d-sm-none">
              {attendance.length === 0 ? (
                <div className="text-muted">No attendance records available.</div>
              ) : (
                <ListGroup variant="flush">
                 {attendanceDaily.map((d) => {
                    const dt = toJS(d.date || d.markedAt || d.createdAt);
                    const ok = !!(d.present ?? d.isPresent ?? d.status === "present");
                    return (
                      <ListGroup.Item key={d.id} className="px-0 d-flex justify-content-between align-items-start">
                        <div className="me-2">
                          <div className="fw-semibold">{dt ? dayjs(dt).format("DD MMM, ddd") : "—"}</div>
                          <div className="small text-muted text-truncate" style={{ maxWidth: 220 }}>
                            {safeTxt(d.markedByName || d.teacherName || d.by || "—")}
                          </div>
                        </div>
                        <Badge bg={ok ? "success" : "danger"}>{ok ? "Present" : "Absent"}</Badge>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Toasts */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast bg="light" onClose={() => setToast((s) => ({ ...s, show: false }))}
               show={toast.show} delay={2200} autohide>
          <Toast.Header>
            <strong className="me-auto">{toast.title || "Notice"}</strong>
            <small>just now</small>
          </Toast.Header>
          <Toast.Body>{toast.body}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Styles */}
      <style>{`
        :root{
          --p: var(--brand-primary, var(--bs-primary));
          --a: var(--brand-accent, var(--bs-primary));
        }
        .progress-shell{ position:relative; }
        .bg-grid{
          position: fixed; inset: 0; z-index: 0; pointer-events:none;
          background:
            radial-gradient(circle at 25% 15%, color-mix(in srgb, var(--p) 12%, transparent), transparent 40%),
            radial-gradient(circle at 85% 85%, color-mix(in srgb, var(--a) 12%, transparent), transparent 45%);
          mask-image:
            radial-gradient(1200px 800px at 50% 30%, rgba(0,0,0,.75), transparent 70%);
        }
        .bg-aurora{
          position: fixed; inset: 0; z-index: 0; pointer-events:none; opacity:.5;
          background-image:
            radial-gradient(600px 280px at 10% 10%, color-mix(in srgb, var(--p) 14%, transparent), transparent 60%),
            radial-gradient(600px 280px at 95% 90%, color-mix(in srgb, var(--a) 14%, transparent), transparent 60%),
            repeating-radial-gradient(circle at 0 0, rgba(255,255,255,.06) 0 1px, transparent 1px 24px);
          mix-blend-mode: screen;
        }
        .card-bento{
          position: relative;
          border: 0;
          border-radius: 16px;
          background:
            linear-gradient(180deg,
              color-mix(in srgb, var(--bs-body-bg) 88%, transparent),
              color-mix(in srgb, var(--bs-body-bg) 94%, transparent));
          backdrop-filter: blur(6px) saturate(115%);
          overflow: hidden;
          box-shadow: 0 10px 28px rgba(0,0,0,.12);
        }
        .card-bento::before{
          content:""; position:absolute; inset:0; padding:1px; border-radius:18px;
          background: linear-gradient(120deg,
            color-mix(in srgb, var(--p) 70%, white 0%),
            color-mix(in srgb, var(--a) 70%, white 0%));
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          pointer-events:none; opacity:.6;
          animation: ringShift 12s linear infinite;
        }
        @keyframes ringShift { 0%{ filter:hue-rotate(0deg) } 100%{ filter:hue-rotate(360deg) } }
        .card-bento:hover{ transform: translateY(-2px); transition: transform .18s ease; }

        .hero{
          background:
            radial-gradient(1200px 480px at 0% 0%, color-mix(in srgb, var(--p) 12%, transparent), transparent 60%),
            radial-gradient(1200px 480px at 100% 100%, color-mix(in srgb, var(--a) 12%, transparent), transparent 60%),
            linear-gradient(180deg, color-mix(in srgb, var(--bs-body-bg) 85%, transparent), color-mix(in srgb, var(--bs-body-bg) 96%, transparent));
        }

        .avatar-ring{ width:56px; height:56px; padding:2px; border-radius:50%;
          background: conic-gradient(from 0deg,
            color-mix(in srgb, var(--p) 70%, white 0%),
            color-mix(in srgb, var(--a) 70%, white 0%));
        }
        .avatar{ width:100%; height:100%; border-radius:50%; display:flex; align-items:center; justify-content:center;
          background: var(--bs-body-bg); color: var(--p); font-size:22px; box-shadow: inset 0 0 0 1px rgba(0,0,0,.1);
        }

        .kpi-head{ display:flex; align-items:center; gap:.5rem; color: var(--bs-body-color); }
        .chip{
          display:inline-flex; align-items:center; justify-content:center;
          width:28px; height:28px; border-radius:8px; color:#fff;
          background: linear-gradient(135deg, color-mix(in srgb, var(--p) 90%, #fff 0%), color-mix(in srgb, var(--a) 90%, #fff 0%));
          box-shadow: inset 0 0 0 1px rgba(255,255,255,.25), 0 4px 12px rgba(0,0,0,.15);
        }
        .chip-sm{ width:22px; height:22px; border-radius:6px; font-size:12px; }

        .bar-glow .progress-bar{
          background-image: linear-gradient(90deg, color-mix(in srgb, var(--p) 96%, transparent), color-mix(in srgb, var(--a) 96%, transparent));
          box-shadow: 0 0 12px color-mix(in srgb, var(--p) 35%, transparent);
        }

        .bento-header, .bento-subheader{ background: transparent; border-bottom: 1px dashed rgba(0,0,0,.08); }
        .card-sub{ border: 0; background: transparent; }
        .table-slim tbody tr > *{ vertical-align: middle; }

        .scrollarea{
          max-height: 320px; overflow:auto; padding-right: 4px;
        }
        .thead-sticky{ position: sticky; top: 0; z-index: 1;
          background: color-mix(in srgb, var(--bs-body-bg) 92%, transparent);
          backdrop-filter: blur(4px);
        }
        /* modern scrollbar */
        .scrollarea::-webkit-scrollbar{ height:10px; width:10px; }
        .scrollarea::-webkit-scrollbar-track{ background: transparent; }
        .scrollarea::-webkit-scrollbar-thumb{
          background: color-mix(in srgb, var(--p) 35%, transparent);
          border-radius: 10px;
        }
        .scrollarea:hover::-webkit-scrollbar-thumb{
          background: color-mix(in srgb, var(--a) 45%, transparent);
        }

        .list-clean .list-group-item{ background: transparent; border: none; }

        .table-hover tbody tr:hover{ background-color: color-mix(in srgb, var(--p) 8%, transparent); }

        @media (max-width: 576px){
          .scrollarea{ max-height: 260px; }
          .bg-aurora{ opacity: .35; }
          .avatar-ring{ width:48px; height:48px; }
        }
      `}</style>
    </div>
  );
}
